import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import * as CryptoJS from 'crypto-js';
import { readCookie, deleteCookie, setSessionCookie } from './csrf-token.util';


@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private readonly legacyAuthStorageKeys = ['accessToken', 'auth.walletAddress'];

  constructor() {
    this.clearLegacyAuthStorage();
  }

  /**
  * store in in localstorage
  * encruption enabled
  * @param {JSON} data
  */
  public storeLoginData(data: any): void {
    if (!data) return;
    const loginData = environment.ENCRYPT_LOCAL_STORAGE ? CryptoJS.AES.encrypt(JSON.stringify(data), environment.LOCAL_STORAGE_SECRET).toString() : JSON.stringify(data);
    localStorage.setItem('login response', loginData);
  }

  /**
   * get stored used data from localstorage
   * @return {JSON} returns user data
   */
  public getLoginData(): any {
    if (!localStorage.getItem('login response')) return null;
    const loginData = environment.ENCRYPT_LOCAL_STORAGE ? CryptoJS.AES.decrypt(localStorage.getItem('login response')!, environment.LOCAL_STORAGE_SECRET).toString(CryptoJS.enc.Utf8) : localStorage.getItem('login response')!;
    return JSON.parse(loginData);
  }

  /**
   * store any data
   *
   * @param {string} key
   * @param {any} value
   */
  storeData(key: string, value: any): void {
    if (this.isLegacyAuthStorageKey(key)) {
      this.clearLegacyAuthStorage();
      return;
    }

    const data = environment.ENCRYPT_LOCAL_STORAGE ? CryptoJS.AES.encrypt(JSON.stringify(value), environment.LOCAL_STORAGE_SECRET).toString() : JSON.stringify(value);
    localStorage.setItem(key, data);
  }

  /**
   * Get localstorage value by key
   *
   * @param {string} key
   * @return {any}
   */
  getDataByKey(key: string): any {
    if (this.isLegacyAuthStorageKey(key)) {
      this.clearLegacyAuthStorage();
      return null;
    }

    if (!localStorage.getItem(key)) return null;
    const data = environment.ENCRYPT_LOCAL_STORAGE ? CryptoJS.AES.decrypt(localStorage.getItem(key)!, environment.LOCAL_STORAGE_SECRET).toString(CryptoJS.enc.Utf8) : localStorage.getItem(key)!;
    return JSON.parse(data);
  }

  /**
   * Params local storage service
   * @param {string}key
   */
  clearLocalStorage(key: string): void {
    if (this.isLegacyAuthStorageKey(key)) {
      this.clearLegacyAuthStorage();
      return;
    }

    localStorage.removeItem(key);
  }

  /**
   * Clears all storage
   */
  clearAllStorage() {
    localStorage.clear();
  }

  /**
   * Marks the current browser runtime as authenticated.
   * The actual session token must be issued by the API as an HttpOnly cookie.
   */
  setToken(_token: string): void {
    setSessionCookie('frontend_has_session', 'true');
    this.clearLegacyAuthStorage();
  }

  /**
   * Gets an in-memory authentication marker for the current browser runtime.
   * The real credential is never exposed to JavaScript after the API sets its cookie.
   */
  getToken(): string | null {
    return readCookie('frontend_has_session') === 'true' ? 'cookie-session' : null;
  }

  /**
   * Removes the in-memory authentication marker and any legacy persisted token.
   */
  removeToken(): void {
    deleteCookie('frontend_has_session');
    this.clearLegacyAuthStorage();
  }

  /**
   *  Sets authenticated wallet address for the current browser runtime only.
   * @param walletAddress
   * */
  setAuthenticatedWallet(walletAddress: string): void {
    setSessionCookie('frontend_auth_wallet', walletAddress);
    this.clearLegacyAuthStorage();
  }

  /**
   * Gets authenticated wallet address from memory.
   * @returns wallet address
   */
  getAuthenticatedWallet(): string | null {
    return readCookie('frontend_auth_wallet');
  }

  /**
   *  Removes authenticated wallet address from memory and any legacy persisted value.
  */
  removeAuthenticatedWallet(): void {
    deleteCookie('frontend_auth_wallet');
    this.clearLegacyAuthStorage();
  }

  /**
   * Removes signup state from local storage
  */
  removeSignupState(): void {
    localStorage.removeItem('signup.state');
  }

  /*
    *  Removes registration state from local storage
  */
  removeRegistrationState(): void {
    localStorage.removeItem('registration.state');
  }

  /*
  * Removes nonce state from local storage
  */
  removeNonceState(): void {
    localStorage.removeItem('nonce.state');
  }

  /*
  * Removes signature state from local storage
  */
  removeSignatureState(): void {
    localStorage.removeItem('signature.state');
  }

  /*
  * Removes user session state from local storage
  */
  removeUserSessionState(): void {
    localStorage.removeItem('user.session.state');
  }

  /*
  * Removes membership state from local storage
  */
  removeMembershipState(): void {
    localStorage.removeItem('membership.state');
  }

  /*
  * Removes holdings state from local storage
  */
  removeHoldingsState(): void {
    localStorage.removeItem('holdings.state');
  }

  /*
  * Removes contribution state from local storage
  */
  removeContributionState(): void {
    localStorage.removeItem('contribution.state');
  }

  /*
  * Removes wallet cached data from local storage
  */
  removeWalletCachedData(): void {
    localStorage.removeItem('wallet.cached.data');
    localStorage.removeItem('wagmi.store');
    localStorage.removeItem('reown.store');
    localStorage.removeItem('@appkit/native_balance_cache');
    localStorage.removeItem('@appkit/token_balance_cache');
    localStorage.removeItem('wagmi.recentConnectorId');
    localStorage.removeItem('@appkit/ens_cache');
    localStorage.removeItem('@appkit/identity_cache');
    localStorage.removeItem('@appkit/active_caip_network_id');
    localStorage.removeItem('@appkit/active_namespace');
    localStorage.removeItem('@appkit/connected_namespaces');
  }

  private clearLegacyAuthStorage(): void {
    for (const key of this.legacyAuthStorageKeys) {
      localStorage.removeItem(key);
    }
  }

  private isLegacyAuthStorageKey(key: string): boolean {
    return this.legacyAuthStorageKeys.includes(key);
  }
}
